/// <amd-module name="Wit/Settings/BoardsExternalIntegration/components/panels/FirstRunExperience/Steps/FirstRunExperienceCreateWorkItemStep" />
import * as React from "react";
import { localeFormat } from "VSS/Core/Util/String";
import { VssComponent } from "VSS/Platform/Layout";
import { Link } from "VSSUI/Link";
import { IWorkItemTrackingRestClient } from "Wit/Clients/Wit/RestClient/WorkItemTracking.Types";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { FirstRunExperienceStep } from "Wit/Settings/BoardsExternalIntegration/components/panels/FirstRunExperience/Steps/FirstRunExperienceStep";
import { StepStatus } from "Wit/Settings/BoardsExternalIntegration/components/panels/FirstRunExperience/Steps/FirstRunExperienceStep.Props";
import { IFirstRunExperienceCreateWorkItemStepProps } from "Wit/Settings/BoardsExternalIntegration/components/panels/FirstRunExperience/Steps/FirstRunExperienceCreateWorkItemStep.Props";
import { DocUrls } from "Wit/Settings/BoardsExternalIntegration/constants/Constants";
import { publishGitHubIntegrationTelemetry, GitHubIntegrationCIActions } from "Wit/Settings/BoardsExternalIntegration/telemetry/GitHub";
import * as Resources from "Wit/Settings/BoardsExternalIntegration/Resources";

const WorkItemDescriptionContentTemplate: string =
    '<b>{0}</b><br><div>{1}<br></div><div><br></div><div>{2}<a href="{3}">{4}</a>{5}<br></div><div><br></div>{6}<a href="{7}">{8}</a>{9}';

export interface IFirstRunExperienceCreateWorkItemStepState {}

export class FirstRunExperienceCreateWorkItemStep extends VssComponent<
    IFirstRunExperienceCreateWorkItemStepProps,
    IFirstRunExperienceCreateWorkItemStepState
> {
    public render(): JSX.Element {
        const { stepId, activeStepId } = this.props;
        return (
            <FirstRunExperienceStep
                stepId={stepId}
                activeStepId={activeStepId}
                onRenderTitle={this._onRenderTitle}
                onRenderError={this._onRenderError}
                onRenderHelpLinks={this._onRenderHelpLinks}
                actionButtonText={Resources.FirstRunExperience_CreateWorkItemStep_ActionButtonText}
                stepProcessingText={Resources.FirstRunExperience_CreateWorkItemStep_ProcessingText}
                onActionButtonClicked={this._onActionButtonClicked}
            />
        );
    }

    private readonly _onRenderTitle = (status: StepStatus): JSX.Element | string => {
        const { boardInfo } = this.props;
        const { defaultWorkItemType, firstColumnName } = boardInfo;
        return status === StepStatus.Succeeded
            ? localeFormat(Resources.FirstRunExperience_CreateWorkItemStep_SuccessTitle, defaultWorkItemType)
            : localeFormat(Resources.FirstRunExperience_CreateWorkItemStep_DefaultTitle, defaultWorkItemType, firstColumnName);
    };

    private readonly _onRenderError = (): JSX.Element | string | undefined => {
        return Resources.FirstRunExperience_CreateWorkItemStep_Error;
    };

    private readonly _onRenderHelpLinks = (status: StepStatus): JSX.Element | undefined => {
        return status === StepStatus.Failed ? (
            <>
                <Link className="step-help-link" target="_blank" href={DocUrls.KanbanBoard_QuickStart}>
                    {Resources.FirstRunExperience_CreateWorkItemStep_Error_GetStartedWithKanbanBoardDoc_Link}
                </Link>
                <Link className="step-help-link" target="_blank" href={DocUrls.GitHubAndAzureBoards_Portal}>
                    {Resources.FirstRunExperience_CreateWorkItemStep_Error_GitHubAndAzureBoardsDoc_Link}
                </Link>
            </>
        ) : (
            undefined
        );
    };

    private readonly _onActionButtonClicked = async (): Promise<StepStatus> => {
        const { projectId, boardInfo, onStepProcessing, onStepSucceeded, onStepFailed } = this.props;
        const { defaultWorkItemType, workItemTeamFieldName, workItemTeamFieldValue } = boardInfo;
        const { pageContext } = this.context;

        try {
            onStepProcessing && onStepProcessing();

            const descriptionContent = localeFormat(
                WorkItemDescriptionContentTemplate,
                Resources.FirstRunExperience_WorkItem_Description_Title,
                Resources.FirstRunExperience_WorkItem_Description_Header,
                Resources.FirstRunExperience_WorkItem_Description_TextParagraph1_Part1,
                DocUrls.GitHubAndAzureBoards_Portal,
                Resources.FirstRunExperience_WorkItem_Description_TextParagraph1_LinkText,
                Resources.FirstRunExperience_WorkItem_Description_TextParagraph1_Part2,
                Resources.FirstRunExperience_WorkItem_Description_TextParagraph2_Part1,
                DocUrls.GitHubAndAzureBoards_BoardBadge,
                Resources.FirstRunExperience_WorkItem_Description_TextParagraph2_LinkText,
                Resources.FirstRunExperience_WorkItem_Description_TextParagraph2_Part2
            );

            const witRestClient = pageContext.getRestClient<IWorkItemTrackingRestClient>("IWorkItemTrackingRestClient");
            const newWorkItem = await witRestClient.createWorkItem(
                [
                    {
                        op: "add",
                        path: `/fields/${CoreFieldRefNames.Title}`,
                        from: null,
                        value: Resources.FirstRunExperience_WorkItem_Title
                    },
                    {
                        op: "add",
                        path: `/fields/${workItemTeamFieldName}`,
                        from: null,
                        value: workItemTeamFieldValue
                    },
                    {
                        op: "add",
                        path: "/fields/System.Description",
                        from: null,
                        value: descriptionContent
                    }
                ] /* document */,
                projectId /* project */,
                defaultWorkItemType /* type */,
                undefined /* validateOnly */,
                true /* bypassRules */
            );

            onStepSucceeded && onStepSucceeded(newWorkItem);

            return StepStatus.Succeeded;
        } catch (error) {
            publishGitHubIntegrationTelemetry(this.context, GitHubIntegrationCIActions.FRE_CREATE_SAMPLE_WORK_ITEM, { error: error.message });
            onStepFailed && onStepFailed(error.message);
            return StepStatus.Failed;
        }
    };
}
